import { graphql } from 'react-relay';
import { JiraVersionAri } from '@atlassian/ari/jira/version';
import type { releaseVersionsIssueTransitionIssueAdjustmentsQuery as releaseVersionsIssueTransitionIssueAdjustmentsQueryType } from '@atlassian/jira-relay/src/__generated__/releaseVersionsIssueTransitionIssueAdjustmentsQuery.graphql';
import { SET_VALUE_ARRAY_SIZE_LIMIT } from '../../../common/constants';
import { FieldValueLookupError } from '../../../common/errors';
import { versionsRequiredScopes as fixVersionsRequiredScopesBase } from '../../../common/fields/versions';
import type { PublicToInternalPropertyTransformerContext } from '../../../common/types';
import type {
	FieldOptionsFilter,
	OptionsVisibility,
} from '../../../common/types/options-visibility';
import { fetchFieldOptions } from '../../../common/utils/fetch-field-options';
import { getResourceIdfromAri } from '../../../common/utils/get-resource-id-from-ari';
import { getValueLookupValuesDiff } from '../../../common/utils/get-value-lookup-values-diff';
import { isNotEmptyString } from '../../../common/utils/is-not-empty-string';

type ReleaseVersionsInternalValue = {
	id: string;
	selectableLabel: string;
	selectableGroupKey?: string | null;
	selectableIconUrl?: string | null;
};

type ReleaseVersionsPublicGetValue = {
	id: string;
	name: string;
};

type ReleaseVersionsPublicSetValue = string[];

export const validateReleaseVersionValue = (
	value: unknown,
): value is ReleaseVersionsPublicSetValue =>
	Array.isArray(value) &&
	value.every(isNotEmptyString) &&
	value.length <= SET_VALUE_ARRAY_SIZE_LIMIT;

// eslint-disable-next-line @atlassian/relay/must-use-inline
export const releaseVersionsIssueTransitionIssueAdjustmentsQuery = graphql`
	query releaseVersionsIssueTransitionIssueAdjustmentsQuery(
		$ari: ID!
		$filterById: JiraFieldOptionIdsFilterInput
	) {
		node(id: $ari) {
			... on JiraMultipleVersionPickerField {
				id
				# eslint-disable-next-line @atlassian/relay/unused-fields
				selectableValueOptions(filterById: $filterById) {
					edges {
						node {
							id
							selectableLabel
							selectableGroupKey
							selectableIconUrl
						}
					}
				}
			}
		}
	}
`;

export const transformReleaseVersionsSetValue = async (
	inputValues: ReleaseVersionsPublicSetValue,
	_lookupValues: undefined,
	{
		tenantContext,
		viewContext,
		environment,
		fieldContext,
	}: PublicToInternalPropertyTransformerContext,
): Promise<ReleaseVersionsInternalValue[]> => {
	if (!inputValues.length) {
		return [];
	}

	const optionARIs = inputValues.map((optionId) =>
		JiraVersionAri.create({
			siteId: tenantContext.cloudId,
			activationId: tenantContext.activationId,
			versionId: optionId,
		}).toString(),
	);

	const response: NonNullable<
		NonNullable<
			releaseVersionsIssueTransitionIssueAdjustmentsQueryType['response']['node']
		>['selectableValueOptions']
	> = await fetchFieldOptions<releaseVersionsIssueTransitionIssueAdjustmentsQueryType>({
		tenantContext,
		viewContext,
		environment,
		fieldContext,
		optionARIs,
		nodePropertyName: 'selectableValueOptions',
		queryName: 'FETCH_RELEASE_VERSIONS',
		query: releaseVersionsIssueTransitionIssueAdjustmentsQuery,
	});

	const fieldOptions =
		response?.edges
			?.map((edge) => edge?.node)
			.filter(Boolean)
			.filter(({ id }) => optionARIs.includes(id))
			.map(({ selectableLabel, id, selectableGroupKey, selectableIconUrl }) => ({
				id,
				selectableLabel: selectableLabel ?? '',
				selectableGroupKey: selectableGroupKey ?? null,
				selectableIconUrl: selectableIconUrl ?? null,
			})) ?? [];

	if (fieldOptions.length !== inputValues.length) {
		const notFoundValues = getValueLookupValuesDiff(optionARIs, fieldOptions, 'id');

		throw new FieldValueLookupError(
			notFoundValues.map((value) => getResourceIdfromAri(value)).join(),
		);
	}

	return fieldOptions;
};

export const transformReleaseVersionsGetValue = (
	internalValues: ReleaseVersionsInternalValue[],
): ReleaseVersionsPublicGetValue[] => {
	if (!internalValues) return [];

	return internalValues.map((value) => ({
		id: getResourceIdfromAri(value.id),
		name: value.selectableLabel,
	}));
};

export const transformReleaseVersionsSetOptionsVisibility = (
	optionsVisibility: OptionsVisibility,
	_lookupValues: undefined,
	{ tenantContext }: PublicToInternalPropertyTransformerContext,
): FieldOptionsFilter => ({
	optionIds: optionsVisibility.options.map((optionId) =>
		JiraVersionAri.create({
			siteId: tenantContext.cloudId,
			activationId: tenantContext.activationId,
			versionId: optionId,
		}).toString(),
	),
	operation: optionsVisibility.isVisible ? 'ALLOW' : 'EXCLUDE',
});

export const releaseVersionsRequiredScopes = {
	...fixVersionsRequiredScopesBase,
	granular: [...fixVersionsRequiredScopesBase.granular, 'write:field.default-value:jira'],
};
